import React, {useState} from 'react';
import {RouteComponentProps} from 'react-router-dom';
import {LocationEntity} from 'grpc-bob/master_data_pb';

import {BootstrapInput, BootstrapLabel, BootstrapSelect} from '../../components/Bootstrap';
import {ReactComponent as GhnLogo} from '../../assets/image/cod-ghn-white.svg';
import {FormControl, FormHelperText, MenuItem} from '@material-ui/core';
import DialogCodResult from '../../components/DialogCodResult';
import DialogCod from '../../components/DialogCod';
import Button from '../../components/Button';

import {useEffectOnce} from 'react-use';
import {useTranslation} from 'react-i18next';
import {useDispatchCtx} from '../../context';

import MasterService from '../../services/master';
import SubscriptionService from '../../services/subscription';
import {AppState, setMessage} from '../../state-management/app';
import getKnowNative from '../../externals/mobile-communication';

import {
  ClientStorage,
  getCommonMsgFromErr,
  appendQuery,
  isEmptyString,
  parseQueryToField
} from '../../utils';

import styles from './Cod.module.css';

interface CodProps extends AppState, RouteComponentProps {
}


function getInitialVal() {
  return {
    value: '',
    err: ''
  }
}

function hasEmptyError(val: string) {
  return isEmptyString(val) ? 'thisFieldIsRequired' : ''
}


const validators: { [x in CodKeys]: (val: string) => string } = {
  fullName: hasEmptyError,
  phoneNumber: hasEmptyError,
  city: hasEmptyError,
  district: hasEmptyError,
  ward: hasEmptyError,
  shippingAddress: hasEmptyError
};

type CodKeys = 'fullName' | 'phoneNumber' | 'city' | 'district' | 'ward' | 'shippingAddress';

type CodInfo = {
  [x in CodKeys]: {
    value: string | number;
    err: string;
  };
};

type DialogResult = {
  open: boolean
  state: 'success' | 'failed'
  message: ''
}

type DialogConfirm = {
  open: boolean,
  isLoading: boolean,
  tickStatus: boolean,
  errStatus: boolean
}


const Cod = (props: CodProps) => {
  const [dialogConfirm, setDialogConfirm] = useState<DialogConfirm>({
    open: false,
    isLoading: false,
    tickStatus: false,
    errStatus: false
  });
  const [dialogResult, setDialogResult] = useState<DialogResult>({
    open: false,
    state: 'success',
    message: ''
  });
  const [currentOrderId, setCurrentOrderId] = useState<number>(0); // 0 for not existed order
  const [currentPackageId, setCurrentPackageId] = useState<number>(0); // 0 for not existed order
  const [cities, setCities] = useState<string[]>([]);
  const [districts, setDistricts] = useState<[number, LocationEntity.AsObject][]>([]);
  const [wards, setWards] = useState<[number, LocationEntity.AsObject][]>([]);
  const [codInfo, setCodInfo] = useState<CodInfo>({
    fullName: {
      value: props.user!.name,
      err: ''
    },
    phoneNumber: {
      value: props.user!.phone,
      err: ''
    },
    city: {
      value: '',
      err: ''
    },
    district: { //set base on array index so value will be number
      value: '',
      err: ''
    },
    ward: {  //set base on array index so value will be number
      value: '',
      err: ''
    },
    shippingAddress: {
      value: '',
      err: ''
    },
  });

  const dispatch = useDispatchCtx();
  const {t} = useTranslation();

  useEffectOnce(() => {
    const {Ref: orderIdInUrl, packageId: packageIdInUrl} = parseQueryToField(props.location.search, ['Ref', 'packageId']);

    if (!props.paymentOrderId && !orderIdInUrl) {
      dispatch(setMessage('orderNotFound'));
      return props.history.push(`/package${props.location.search}`);
    }

    const packageId = props.paymentOrderId ? props.paymentOrderId : packageIdInUrl;

    if (!packageId) {
      dispatch(setMessage('packageNotFound'));
      return props.history.push(`/package${props.location.search}`);
    }

    setCurrentOrderId(Number(orderIdInUrl));
    setCurrentPackageId(Number(packageId))
  });

  useEffectOnce(() => {
    const importantCities = ["Cần Thơ", "Đà Nẵng", "Hà Nội", "Hồ Chí Minh"];

    async function fetchCities() {
      try {
        const cities = await MasterService.getCities(props.token as string);
        const citiesSorted = cities.sort();

        importantCities.forEach((importantCity) => { //sort important city first
          const index = citiesSorted.findIndex((city) => city === importantCity);

          const spliced = citiesSorted.splice(index, 1);
          citiesSorted.unshift(spliced[0]);
        });

        setCities(citiesSorted);
      } catch (e) {
      }
    }

    fetchCities();
  });

  const onChangeInputFor = (codField: string) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      setCodInfo({
        ...codInfo,
        [codField]: {
          value: e.target.value,
          err: ''
        }
      })
    }
  };

  const onChangeCities = async (e: React.ChangeEvent<{ value: unknown }>) => {
    const isSameCity = !isEmptyString(e.target.value as string) && e.target.value === codInfo.city.value;

    if (isSameCity) {
      return;
    }

    setCodInfo({
      ...codInfo,
      city: {
        value: e.target.value as string,
        err: ''
      },
      district: getInitialVal(),
      ward: getInitialVal(),
    });

    const districts = await MasterService.getWards(e.target.value as string, props.token as string);

    setDistricts(districts);
    setWards([]);
  };

  //for ward & district, set index of itself in array instead of name
  const onChangeDistrict = async (e: React.ChangeEvent<{ value: unknown }>) => {
    const isSameDistrict = !isEmptyString(e.target.value as string) && e.target.value === codInfo.district.value;

    if (isSameDistrict) return;

    setCodInfo({
      ...codInfo,
      district: {
        value: e.target.value as string,
        err: ''
      },
      ward: getInitialVal(),
    });

    setWards(districts[e.target.value as any][1].subMap);
  };

  const onChangeWard = (e: React.ChangeEvent<{ value: unknown }>) => {
    const {value} = e.target;
    const isSameWard = !isEmptyString(value as string) && value === codInfo.ward.value;

    if (isSameWard) return;

    setCodInfo({
      ...codInfo,
      ward: {
        value: value as string,
        err: ''
      },
    });
  };

  const onConfirmPayment = () => {
    if (!isCodInfoValid()) {
      dispatch(setMessage('pleaseFillForm'));
      return;
    }
    if (window.document.activeElement) { //remove focus for active element on the page
      //@ts-ignore
      window.document.activeElement.blur();
    }

    setDialogConfirm({
      ...dialogConfirm,
      open: true
    });
  };

  const isCodInfoValid = () => {
    const validator = {...codInfo};
    let hasNoErr = true;

    //@ts-ignore
    Object.keys(codInfo).forEach((field: CodKeys) => { //todo using map for codInfo for typesafe
      const errMessage = validators[field](codInfo[field].value as string);

      if (errMessage) {
        validator[field].err = errMessage;
        hasNoErr = false;
      }
    });

    if (hasNoErr) {
      setCodInfo(validator);
    }

    return hasNoErr;
  };

  const onConfirmLastTime = async () => {
    if (!dialogConfirm.tickStatus) {
      setDialogConfirm({...dialogConfirm, errStatus: true});
      return;
    }

    setDialogConfirm({...dialogConfirm, isLoading: true});

    if (!currentPackageId) {
      setDialogConfirm({...dialogConfirm, isLoading: false});
      return dispatch(setMessage('packageNotFound'));
    }

    const params = {
      orderId: currentOrderId,
      packageId: currentPackageId,
      studentId: props.user!.id,
      fullName: codInfo.fullName.value,
      district: districts[codInfo.district.value as any][0],
      ward: wards[codInfo.ward.value as any][0],
      promotionCode: props.promotion.text,
      phoneNumber: codInfo.phoneNumber.value,
      shippingAddress: codInfo.shippingAddress.value
    };

    try {
      const res = await SubscriptionService.createOrderCod(params, props.token as string);

      setCurrentOrderId(res.orderId);

      setDialogResult({
        open: true,
        message: '',
        state: 'success'
      })
    } catch (e) {
      const msg = getCommonMsgFromErr(e);

      setDialogResult({
        open: true,
        message: msg ? t(msg) : t(e.message),
        state: 'failed'
      })
    } finally {
      setDialogConfirm({
        ...dialogConfirm,
        open: false,
        isLoading: false,
        tickStatus: false,
        errStatus: false
      });
    }
  };

  const onTickChanged = (e: any) => {
    setDialogConfirm({
      ...dialogConfirm,
      tickStatus: e.target.checked,
      errStatus: false
    });
  };

  const onCloseConfirm = () => {
    setDialogConfirm({...dialogConfirm, open: false, errStatus: false, tickStatus: false})
  };

  const onEndPaymentCod = () => {
    if (dialogResult.state === 'success') {
      const newSearchQuery = appendQuery(props.location.search, 'Ref', String(currentOrderId));
      ClientStorage.token = props.token!;

      return props.history.push(`/summary?${newSearchQuery}`);
    }

    setDialogResult({
      ...dialogResult,
      message: '',
      open: false
    })
  };

  return (
    <div className={styles.root}>
      <div>
        <div className={styles.ghnDesc}>
          <GhnLogo/>
          <p>
            {t('ghnDesc')}
          </p>
        </div>
        <div className={styles.codFormWrapper}>
          <div className={styles.codPayer}>{t('payerInfo')}</div>
          <div className={styles.form}>
            <FormControl className={styles.formField} error={!!codInfo.fullName.err}>
              <BootstrapLabel
                shrink
                required
                htmlFor="input-fullName"
              >
                {t('fullName')}
              </BootstrapLabel>
              <BootstrapInput
                id="input-fullName"
                fullWidth
                value={codInfo.fullName.value}
                onChange={onChangeInputFor('fullName')}
              />
              <FormHelperText id="input-fullName">{t(codInfo.fullName.err)}</FormHelperText>
            </FormControl>
            <FormControl className={styles.formField} error={!!codInfo.phoneNumber.err}>
              <BootstrapLabel
                shrink
                required
                htmlFor="input-phoneNumber"
              >
                {t('phoneNumber')}
              </BootstrapLabel>
              <BootstrapInput
                id="input-phoneNumber"
                fullWidth
                placeholder="(+84)___-___-___"
                value={codInfo.phoneNumber.value}
                onChange={onChangeInputFor('phoneNumber')}
              />
              <FormHelperText id="input-phoneNumber">{t(codInfo.phoneNumber.err)}</FormHelperText>
            </FormControl>
            <FormControl
              className={styles.formField}
              error={!!codInfo.city.err && cities.length > 0}
            >
              <BootstrapLabel
                shrink
                required
                htmlFor="input-cities"
              >
                {t('city')}
              </BootstrapLabel>
              <BootstrapSelect
                id="input-cities"
                fullWidth
                value={codInfo.city.value}
                variant="outlined"
                onChange={onChangeCities}
              >
                {
                  cities.map((city, index) => {
                    return <MenuItem key={index} value={city}>{city}</MenuItem>
                  })
                }
              </BootstrapSelect>
              <FormHelperText id="input-cities">{t(codInfo.city.err)}</FormHelperText>
            </FormControl>
            <FormControl
              className={styles.formField}
              error={!!codInfo.district.err}
              disabled={isEmptyString(codInfo.city.value as string) || districts.length <= 0}
            >
              <BootstrapLabel
                shrink
                required
                htmlFor="input-districts"
              >
                {t('district')}
              </BootstrapLabel>
              <BootstrapSelect
                id="input-districts"
                fullWidth
                value={codInfo.district.value}
                variant="outlined"
                onChange={onChangeDistrict}
              >
                {
                  districts.map((district, index) => {
                    return <MenuItem key={district[0]} value={index}>{district[1].name}</MenuItem>
                  })
                }
              </BootstrapSelect>
              <FormHelperText id="input-districts">{t(codInfo.district.err)}</FormHelperText>
            </FormControl>
            <FormControl
              className={styles.formField}
              error={!!codInfo.ward.err}
              disabled={isEmptyString(codInfo.district.value as string) || wards.length <= 0}
            >
              <BootstrapLabel
                shrink
                required
                htmlFor="input-wards"
              >
                {t('ward')}
              </BootstrapLabel>
              <BootstrapSelect
                id="input-wards"
                fullWidth
                value={codInfo.ward.value}
                variant="outlined"
                onChange={onChangeWard}
              >
                {
                  wards.map((ward, index) => {
                    return <MenuItem key={ward[0]} value={index}>{ward[1].name}</MenuItem>
                  })
                }
              </BootstrapSelect>
              <FormHelperText id="input-wards">{t(codInfo.ward.err)}</FormHelperText>
            </FormControl>
            <FormControl
              className={styles.formField}
              error={!!codInfo.shippingAddress.err}
            >
              <BootstrapLabel
                shrink
                required
                htmlFor="input-shippingAddress"
              >
                {t('shippingAddress')}
              </BootstrapLabel>
              <BootstrapInput
                id="input-shippingAddress"
                fullWidth
                value={codInfo.shippingAddress.value}
                onChange={onChangeInputFor('shippingAddress')}
                onClick={() => getKnowNative('inputFocus')}
              />
              <FormHelperText id="input-shippingAddress">{t(codInfo.shippingAddress.err)}</FormHelperText>
            </FormControl>
            <Button className={styles.button} onClick={onConfirmPayment}>
              {t('confirmPayment')}
            </Button>
          </div>
        </div>
      </div>
      <DialogCod
        open={dialogConfirm.open}
        isLoading={dialogConfirm.isLoading}
        tickStatus={dialogConfirm.tickStatus}
        errStatus={dialogConfirm.errStatus}
        onConfirm={onConfirmLastTime}
        onClose={onCloseConfirm}
        onTickChanged={onTickChanged}
      />
      <DialogCodResult
        open={dialogResult.open}
        type={dialogResult.state}
        message={dialogResult.message}
        onClose={onEndPaymentCod}
        onConfirm={onEndPaymentCod}
      />
    </div>
  );
};

export default Cod;
